/**
 * Email Parser — brain-powered order item extraction.
 * Uses LLM (Groq cloud or local Gemma) to parse structured order data from cleaned email text.
 * Port of mittens parseOrders.ts.
 */

import { getBrain } from './webBrain';
import { BACKEND_URL } from '../utils/config';

// Check if backend is v5
const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';
const apiPrefix = isV5 ? '/api' : '';

let brandTemplatesCache = null;

async function fetchBrandTemplates() {
  if (brandTemplatesCache) return brandTemplatesCache;
  try {
    const res = await fetch(`${BACKEND_URL}${apiPrefix}/brand-extraction-templates?_limit=100`);
    if (res.ok) {
      brandTemplatesCache = await res.json();
      return brandTemplatesCache;
    }
  } catch (err) {
    console.warn('[Parser] Failed to fetch brand templates:', err);
  }
  return [];
}

/**
 * Extract order items from a single cleaned email.
 * @param {object} email - Email candidate with cleanedBody and optional imageUrls
 * @param {function} onBrainProgress - Optional progress callback for model loading
 * @returns {Promise<Array>} Array of parsed order items
 */
export async function parseOrderEmail(email, onBrainProgress) {
  if (!email.cleanedBody) {
    console.warn(`[Parser] Skipping "${email.subject}" — empty cleanedBody`);
    return [];
  }

  console.log(`[Parser] Parsing "${email.subject}", bodyLen=${email.cleanedBody.length}, images=${email.imageUrls?.length || 0}`);

  // 1. Check for brand templates first (Deterministic Extraction)
  const templates = await fetchBrandTemplates();
  const matchedTemplate = templates.find(t => 
    email.from.toLowerCase().includes(t.brand.toLowerCase()) || 
    email.subject.toLowerCase().includes(t.brand.toLowerCase())
  );

  if (matchedTemplate && matchedTemplate.config) {
    console.log(`[Parser] Using deterministic template for brand: ${matchedTemplate.brand}`);
    try {
      // Very simple deterministic extraction based on regex from config
      // A real system would use a robust JSONPath/Regex engine
      const config = matchedTemplate.config;
      let orderNumberMatch = null;
      if (config.orderNumberRegex) {
        const match = email.cleanedBody.match(new RegExp(config.orderNumberRegex, 'i'));
        if (match) orderNumberMatch = match[1] || match[0];
      }
      
      // We will fallback to AI if template doesn't specify item extraction yet
      // This allows gradual migration to templates
      if (config.extractItems !== false) {
        // Just as a placeholder for the architecture, we'll still use AI but we could 
        // inject the orderNumber found by regex.
        console.log(`[Parser] Template found order number: ${orderNumberMatch}`);
      } else {
        // Return structured data immediately if template fully processes it
      }
    } catch (err) {
      console.warn(`[Parser] Template extraction failed for ${matchedTemplate.brand}, falling back to AI:`, err);
    }
  }

  // 2. Fallback to AI Extraction
  const brain = await getBrain(onBrainProgress);

  const imageContext = email.imageUrls && email.imageUrls.length > 0
    ? `\nExtracted image URLs from this email: ${JSON.stringify(email.imageUrls)}`
    : '';

  // Truncate body to avoid exceeding token limits, but keep enough for extraction
  const body = email.cleanedBody.slice(0, 3000);

  const prompt = `You are a JSON extraction tool. Extract clothing/fashion order items from this email and return ONLY a JSON array. No explanations, no code, no markdown fences.

Email from: ${email.from}
Subject: ${email.subject}
Body: "${body}"${imageContext}

Return ONLY a raw JSON array (no \`\`\` markers, no explanation):
[{"n":"item name","b":"brand","p":{"a":price,"c":"USD"},"sz":"size","cl":"color","cat":"dress|top|bottom|shoes|bag|accessory|outerwear|other","img":"image url if available","on":"order number","d":"YYYY-MM-DD","r":"retailer","st":"ordered|shipped|delivered"}]

Rules:
- Return ONLY the JSON array, nothing else
- Extract ALL clothing/fashion items
- "cat" must be one of: dress, top, bottom, shoes, bag, accessory, outerwear, other
- If an image URL from the list matches an item, use it for "img"
- If no image URL, omit "img"
- If price not found, set "a" to 0
- If item name is unclear, use the best description from the email
- If no fashion items found, return []`;

  const raw = await brain.text(prompt, { temperature: 0.1 });
  console.log(`[Parser] Raw LLM response for "${email.subject}":`, raw.slice(0, 500));
  return parseOrderResponse(raw, email);
}

/**
 * Parse order items from multiple emails.
 *
 * Handles 402 insufficient_credits mid-batch:
 *   - Stops processing at the email that triggered the paywall
 *   - Returns all items parsed so far (they're kept, not lost)
 *   - Sets result.paywallHit = true with resume info
 *   - Caller shows "Imported X of N — upgrade to finish" and can resume
 *
 * @param {Array} emails - Email candidates to parse
 * @param {Function} onBrainProgress - Progress callback for model loading
 * @param {Function} onProgress - Per-email progress callback: (processed, total, items) => void
 * @param {number} startIndex - Resume from this index (0 = start, used after top-up)
 * @returns {{ items: Array, processed: number, total: number, paywallHit?: boolean, paywallInfo?: object }}
 */
export async function parseOrderEmails(emails, onBrainProgress, onProgress, startIndex = 0) {
  const allItems = [];
  const total = emails.length;

  for (let i = startIndex; i < total; i++) {
    const email = emails[i];
    try {
      const items = await parseOrderEmail(email, onBrainProgress);
      console.log(`[Parser] "${email.subject}" → ${items.length} items`);
      allItems.push(...items);

      if (onProgress) {
        onProgress(i + 1, total, allItems);
      }
    } catch (err) {
      // 402 insufficient credits → stop batch, return partial results
      if (err.reason === 'insufficient_credits') {
        console.warn(`[Parser] Paywall hit at email ${i + 1}/${total}:`, err.message);
        return {
          items: allItems,
          processed: i,
          total,
          paywallHit: true,
          paywallInfo: {
            needed: err.needed,
            balance: err.balance,
            options: err.options,
            requestId: err.requestId,
            resumeIndex: i,
          },
        };
      }

      console.error(`[Parser] Error parsing "${email.subject}":`, err);
      // Non-402 errors: skip this email, continue batch
    }
  }

  console.log(`[Parser] Total items from all emails: ${allItems.length}`);
  return { items: allItems, processed: total, total, paywallHit: false };
}

/**
 * Extract JSON array from raw LLM response, handling markdown fences and extra text.
 */
function extractJsonArray(raw) {
  // 1. Try to extract from markdown code blocks first: ```json ... ``` or ``` ... ```
  const fenceMatch = raw.match(/```(?:json)?\s*(\[[\s\S]*?\])\s*```/);
  if (fenceMatch) {
    console.log('[Parser] Extracted JSON from markdown fence');
    return fenceMatch[1];
  }

  // 2. Try to find a standalone JSON array (non-greedy, stop at first complete array)
  // Find the first '[' and try to parse progressively
  const startIdx = raw.indexOf('[');
  if (startIdx === -1) return null;

  // Find matching closing bracket by counting depth
  let depth = 0;
  for (let i = startIdx; i < raw.length; i++) {
    if (raw[i] === '[') depth++;
    else if (raw[i] === ']') {
      depth--;
      if (depth === 0) {
        const candidate = raw.slice(startIdx, i + 1);
        console.log('[Parser] Extracted JSON by bracket matching, length:', candidate.length);
        return candidate;
      }
    }
  }

  // 3. Fallback: greedy regex (last resort)
  const greedyMatch = raw.match(/\[[\s\S]*\]/);
  if (greedyMatch) {
    console.log('[Parser] Extracted JSON by greedy regex (fallback)');
    return greedyMatch[0];
  }

  return null;
}

/**
 * Parse the raw AI response into structured order items.
 */
function parseOrderResponse(raw, email) {
  try {
    const jsonStr = extractJsonArray(raw);
    if (!jsonStr) {
      console.warn(`[Parser] No JSON array found in response for "${email.subject}"`);
      return [];
    }

    const parsed = JSON.parse(jsonStr);
    if (!Array.isArray(parsed)) {
      console.warn(`[Parser] Parsed result is not an array for "${email.subject}"`);
      return [];
    }

    console.log(`[Parser] Parsed ${parsed.length} raw items from "${email.subject}":`, parsed);

    return parsed.map(item => ({
      itemName: item.n || item.itemName || 'Unknown Item',
      brand: item.b || item.brand || '',
      price: item.p ? {
        amount: item.p.a || item.p.amount || 0,
        currency: item.p.c || item.p.currency || 'USD'
      } : { amount: 0, currency: 'USD' },
      size: item.sz || item.size || '',
      color: item.cl || item.color || '',
      category: item.cat || item.category || 'other',
      imageUrl: item.img || item.imageUrl || (email.imageUrls && email.imageUrls.length > 0 ? email.imageUrls[0] : ''),
      orderNumber: item.on || item.orderNumber || '',
      orderDate: item.d || item.orderDate || email.date || '',
      retailer: item.r || item.retailer || email.retailer || '',
      status: item.st || item.status || 'ordered',
      // Track source email for deduplication
      sourceEmailId: email.id,
      sourceSubject: email.subject
    }));
  } catch (err) {
    console.error(`[Parser] JSON parse error for "${email.subject}":`, err.message);
    console.error(`[Parser] Attempted to parse:`, raw.slice(0, 400));
    return [];
  }
}
